import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { formatTimestamp } from '@/hooks/useActivity';
import { useAuth } from '@/hooks/useAuth';
import { resolveAvatarUrl } from '@/lib/avatar';
import { useBlockedUserIds } from '@/hooks/useBlockedUsers';
import { useCallback, useEffect, useMemo } from 'react';

const DEV = import.meta.env.DEV;
const devLog = (...args: unknown[]) => {
  if (DEV) console.log(...args);
};

export interface Notification {
  id: string;
  type:
    | 'follow'
    | 'like'
    | 'comment'
    | 'comment_reply'
    | 'comment_on_review'
    | 'mention'
    | 'follow_request'
    | 'follow_request_approved'
    | 'artist_nearby_show'
    | 'review'
    | 'activity'
    | 'list_create'
    | 'concert_announced'
    | 'tour_announced';
  message: string;
  timestamp: string;
  createdAt: string;
  read: boolean;
  /** True when this row lives in public.notifications (server-backed read state). */
  serverBacked?: boolean;
  user?: {
    username: string;
    name: string;
    avatar: string;
  };
  metadata?: any;
}

// Page sizing for progressive loading. We over-fetch raw activities because
// post-filtering (targeted to me, self-skip, dedupe) can drop several rows.
const RAW_PAGE_SIZE = 60;
const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;

/**
 * Read-state model.
 *
 * Direct rows (public.notifications) have a `read` column, so their read state
 * is persisted server side and survives device changes. Activity-derived rows
 * (public.activities) have no per-recipient row, so their read state remains
 * local-device only (read ids + a last-seen watermark in localStorage). Should
 * a per-recipient activity read field ever exist, only `resolveRead` and the
 * mutation helpers below need to change — the UI contract stays identical.
 */
const sharedState = {
  readIds: new Set<string>(),
  lastSeenAt: null as string | null,
  userId: null as string | null,
  initialized: false,
  /** Ids known to live in public.notifications (updatable server side). */
  serverBackedIds: new Set<string>(),
};

const getStorageKey = (userId: string) => `sc_notifications_read_ids:${userId}`;
const getLastSeenKey = (userId: string) => `sc_notifications_last_seen:${userId}`;

const loadReadIds = (userId: string): Set<string> => {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return new Set<string>();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set<string>();
    return new Set<string>(parsed.filter((x: unknown): x is string => typeof x === 'string'));
  } catch {
    return new Set<string>();
  }
};

const loadLastSeenAt = (userId: string): string | null => {
  try {
    const raw = localStorage.getItem(getLastSeenKey(userId));
    return raw && typeof raw === 'string' ? raw : null;
  } catch {
    return null;
  }
};

const persistReadIds = () => {
  const userId = sharedState.userId;
  if (!userId) return;
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(Array.from(sharedState.readIds)));
  } catch {
    // ignore storage errors
  }
};

const persistLastSeenAt = () => {
  const userId = sharedState.userId;
  if (!userId) return;
  try {
    if (sharedState.lastSeenAt) {
      localStorage.setItem(getLastSeenKey(userId), sharedState.lastSeenAt);
    }
  } catch {
    // ignore storage errors
  }
};

/**
 * Read resolution for a single notification row.
 *
 * A row is read when the server says so, when this device marked it read, or
 * when it predates the user's last-seen watermark. Exported for tests.
 */
export const isNotificationRead = ({
  id,
  createdAt,
  serverRead,
  readIds,
  lastSeenAt,
}: {
  id: string;
  createdAt: string | null;
  serverRead?: boolean | null;
  readIds: Set<string>;
  lastSeenAt: string | null;
}): boolean => {
  if (serverRead) return true;
  if (readIds.has(id)) return true;
  if (!createdAt) return true;
  if (!lastSeenAt) return false;
  const createdMs = new Date(createdAt).getTime();
  const seenMs = new Date(lastSeenAt).getTime();
  if (Number.isNaN(createdMs) || Number.isNaN(seenMs)) return true;
  return createdMs <= seenMs;
};

const resetSharedState = () => {
  sharedState.readIds = new Set<string>();
  sharedState.serverBackedIds = new Set<string>();
  sharedState.lastSeenAt = null;
  sharedState.userId = null;
  sharedState.initialized = false;
};

const initializeForUser = (userId: string) => {
  if (sharedState.userId === userId && sharedState.initialized) return;

  sharedState.userId = userId;
  sharedState.readIds = loadReadIds(userId);
  sharedState.serverBackedIds = new Set<string>();

  // First load for this user/device: everything that already exists counts as
  // seen. The old 7-day lookback marked a week of pre-existing history unread
  // on every fresh device, which is what lit the bottom-nav dot with nothing
  // actually new to read.
  const storedLastSeen = loadLastSeenAt(userId);
  sharedState.lastSeenAt = storedLastSeen || new Date().toISOString();

  if (!storedLastSeen) {
    try {
      localStorage.setItem(getLastSeenKey(userId), sharedState.lastSeenAt);
    } catch {
      // ignore storage errors
    }
  }
  sharedState.initialized = true;
};

interface NotificationPage {
  items: Notification[];
  nextCursor: string | null;
  rawCount: number;
}

const NOTIFICATION_ACTIVITY_TYPES = [
  'follow',
  'like',
  'comment',
  'comment_reply',
  'tour_announced',
  'mention',
] as const;

const DIRECT_NOTIFICATION_TYPES = [
  'comment_on_review',
  'comment_reply',
  'follow_request',
  'follow_request_approved',
  'artist_nearby_show',
] as const;

// Comment activity rows are duplicated by canonical direct notification rows.
// Suppress the activity copy when a direct row for the same review lands
// within this window.
const COMMENT_DUPE_WINDOW_MS = 5 * 60 * 1000;

type ActivityRow = {
  id: string;
  user_id: string;
  type: string;
  target_id?: string | null;
  target_type?: string | null;
  metadata?: Record<string, any> | null;
  created_at: string;
};

type DirectNotificationRow = {
  id: string;
  user_id: string;
  type: string;
  message?: string | null;
  related_id?: string | null;
  read?: boolean | null;
  created_at: string;
};

type ProfileLite = { id?: string; username?: string | null; name?: string | null; profile_picture?: string | null };
type ReviewLite = { id: string; user_id?: string | null; artist_name?: string | null; tour_name?: string | null };
type ListLite = { id: string; name?: string | null; user_id?: string | null };
type ArtistLite = { id: string; name?: string | null; image?: string | null };
type ConcertLite = {
  id: string;
  slug?: string | null;
  venue?: string | null;
  city?: string | null;
  date?: string | null;
  artist_id?: string | null;
  image?: string | null;
};

const uniq = (values: (string | null | undefined)[]): string[] =>
  Array.from(new Set(values.filter((v): v is string => typeof v === 'string' && v.length > 0)));

/** Chunked `.in()` lookups — the platform caps `.in()` filters at 100 items. */
async function fetchByIds<T>(
  table: 'profiles' | 'reviews' | 'user_lists' | 'artists' | 'concerts',
  columns: string,
  ids: string[]
): Promise<Map<string, T>> {
  const map = new Map<string, T>();
  if (ids.length === 0) return map;
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += 100) chunks.push(ids.slice(i, i + 100));
  const results = await Promise.all(
    chunks.map((chunk) => supabase.from(table).select(columns).in('id', chunk))
  );
  results.forEach(({ data, error }) => {
    if (error) {
      console.error(`Error batch-loading ${table}:`, error);
      return;
    }
    (data as any[] | null)?.forEach((row) => {
      if (row?.id) map.set(row.id as string, row as T);
    });
  });
  return map;
}

export const useNotifications = () => {
  const { user, loading } = useAuth();
  const queryClient = useQueryClient();
  const { blockedIds } = useBlockedUserIds();
  const blockedKey = Array.from(blockedIds).sort().join(',');
  const queryKey = [...NOTIFICATIONS_QUERY_KEY, user?.id, blockedKey] as const;

  const query = useInfiniteQuery<NotificationPage>({
    queryKey,
    enabled: !loading && !!user,
    // Social surface: refresh in the background when the user deliberately
    // revisits after the data has gone stale. Cached pages stay on screen.
    staleTime: 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      if (!user) return { items: [], nextCursor: null, rawCount: 0 };

      const userId = user.id;
      initializeForUser(userId);

      let q = supabase
        .from('activities')
        .select('*')
        .in('type', [...NOTIFICATION_ACTIVITY_TYPES])
        .order('created_at', { ascending: false })
        .limit(RAW_PAGE_SIZE);
      if (pageParam) q = q.lt('created_at', pageParam as string);

      const [{ data: activities, error }, { data: directRows, error: directError }] = await Promise.all([
        q,
        supabase
          .from('notifications')
          .select('*')
          .eq('user_id', userId)
          .in('type', [...DIRECT_NOTIFICATION_TYPES])
          .order('created_at', { ascending: false })
          .lt('created_at', pageParam || '9999-12-31T23:59:59.999Z')
          .limit(RAW_PAGE_SIZE),
      ]);
      if (error) console.error('Error fetching notifications feed:', error);
      if (directError) console.error('Error fetching direct notifications feed:', directError);

      const rawList = (activities || []) as ActivityRow[];
      const directList = (directRows || []) as DirectNotificationRow[];
      devLog('[NotificationsPagination] fetch', {
        tab: 'notifications',
        pageParam,
        requested: RAW_PAGE_SIZE,
        returned: rawList.length,
        directReturned: directList.length,
      });

      directList.forEach((n) => sharedState.serverBackedIds.add(n.id));

      // Canonical direct comment rows (review_id + time) so we can suppress
      // the duplicate activity copy of the same event.
      const directCommentRows = directList.filter(
        (n) => n.type === 'comment_on_review' || n.type === 'comment_reply'
      );
      const hasDirectCommentCopy = (reviewId: string | undefined, createdAt: string) => {
        if (!reviewId) return false;
        const at = new Date(createdAt).getTime();
        if (Number.isNaN(at)) return false;
        return directCommentRows.some((n) => {
          if (n.related_id !== reviewId) return false;
          const dt = new Date(n.created_at).getTime();
          return !Number.isNaN(dt) && Math.abs(dt - at) <= COMMENT_DUPE_WINDOW_MS;
        });
      };

      // Filter to activities where the current user is the target
      const targetedActivities = rawList.filter((activity: any) => {
        const meta = activity.metadata || {};
        const isNotSelf = activity.user_id !== userId;
        if (blockedIds.has(activity.user_id)) return false;

        if (activity.type === 'tour_announced') {
          return activity.user_id === userId || activity.target_id === userId;
        }
        if (activity.type === 'follow') {
          return isNotSelf && (activity.target_id === userId || meta.following_id === userId);
        }
        if (activity.type === 'mention') {
          return isNotSelf && (activity.target_id === userId || meta.target_user_id === userId);
        }
        if (activity.type === 'comment' || activity.type === 'comment_reply') {
          if (hasDirectCommentCopy(meta.review_id, activity.created_at)) return false;
        }
        const isTargetUser = meta.target_user_id === userId || activity.target_id === userId;
        return isTargetUser && isNotSelf;
      });

      // ---------------------------------------------------------------
      // Batched enrichment: collect every id first, then run a small number
      // of grouped lookups instead of one query per notification row.
      // ---------------------------------------------------------------
      const profileIds: string[] = targetedActivities.map((a) => a.user_id);
      const reviewIds: string[] = [];
      const listIds: string[] = [];
      const artistIds: string[] = [];
      const concertIds: string[] = [];
      const actorNames: string[] = [];

      targetedActivities.forEach((activity) => {
        const meta = activity.metadata || {};
        if (activity.type === 'mention') {
          if (meta.source === 'list_comment' && meta.list_id) listIds.push(meta.list_id);
          else if (meta.review_id) reviewIds.push(meta.review_id);
        } else if (activity.type === 'comment' && meta.review_id) {
          reviewIds.push(meta.review_id);
        } else if (activity.type === 'like') {
          const itemId = meta.item_id || meta.review_id;
          if (meta.item_type === 'review' && itemId) reviewIds.push(itemId);
          if (meta.item_type === 'list' && itemId) listIds.push(itemId);
        } else if (activity.type === 'tour_announced' && meta.artist_id) {
          artistIds.push(meta.artist_id);
        }
      });

      directList.forEach((row) => {
        const relatedId = row.related_id || undefined;
        const type = row.type;
        if ((type === 'follow_request' || type === 'follow_request_approved') && relatedId) {
          profileIds.push(relatedId);
        } else if (type !== 'artist_nearby_show') {
          const actorMatch = /^(.*?)\s+(liked|commented|replied|mentioned|started following|sent you)/i.exec(
            row.message || ''
          );
          const actorName = actorMatch?.[1]?.trim();
          if (actorName) actorNames.push(actorName);
        }
        if ((type === 'comment_on_review' || type === 'comment_reply' || type === 'mention') && relatedId) {
          reviewIds.push(relatedId);
        }
        if (type === 'artist_nearby_show' && relatedId) concertIds.push(relatedId);
      });

      const uniqueNames = uniq(actorNames);
      const [profileMap, reviewMap, listMap, concertMap, nameProfiles] = await Promise.all([
        fetchByIds<ProfileLite>('profiles', 'id, username, name, profile_picture', uniq(profileIds)),
        fetchByIds<ReviewLite>('reviews', 'id, user_id, artist_name, tour_name', uniq(reviewIds)),
        fetchByIds<ListLite>('user_lists', 'id, name, user_id', uniq(listIds)),
        fetchByIds<ConcertLite>('concerts', 'id, slug, venue, city, date, artist_id, image', uniq(concertIds)),
        uniqueNames.length
          ? supabase
              .from('profiles')
              .select('id, username, name, profile_picture')
              .in('name', uniqueNames)
              .then(({ data }) => (data || []) as ProfileLite[])
          : Promise.resolve([] as ProfileLite[]),
      ]);

      // Name-matched actor profiles (case-insensitive map, matching the old
      // per-row `ilike` behaviour). Any name the exact-match batch missed gets
      // a single targeted `ilike` lookup — rare in practice.
      const nameProfileMap = new Map<string, ProfileLite>();
      nameProfiles.forEach((p) => {
        if (p.name) nameProfileMap.set(p.name.toLowerCase(), p);
      });
      const missingNames = uniqueNames.filter((n) => !nameProfileMap.has(n.toLowerCase()));
      if (missingNames.length > 0) {
        const fallbacks = await Promise.all(
          missingNames.map((n) =>
            supabase
              .from('profiles')
              .select('id, username, name, profile_picture')
              .ilike('name', n)
              .limit(1)
              .maybeSingle()
              .then(({ data }) => ({ name: n, profile: data as ProfileLite | null }))
          )
        );
        fallbacks.forEach(({ name, profile }) => {
          if (profile) nameProfileMap.set(name.toLowerCase(), profile);
        });
      }

      // Second (and final) round: artists referenced directly plus artists
      // discovered via the nearby-show concerts.
      const concertArtistIds = Array.from(concertMap.values()).map((c) => c.artist_id || undefined);
      const artistMap = await fetchByIds<ArtistLite>(
        'artists',
        'id, name, image',
        uniq([...artistIds, ...concertArtistIds])
      );

      const resolveRead = (id: string, createdAtRaw: string | null, serverRead?: boolean | null) =>
        isNotificationRead({
          id,
          createdAt: createdAtRaw,
          serverRead,
          readIds: sharedState.readIds,
          lastSeenAt: sharedState.lastSeenAt,
        });


      const activityNotifications: Notification[] = targetedActivities.map((activity) => {
        const profile = profileMap.get(activity.user_id) || null;
        const activityType = activity.type;
        let message = `${profile?.name || 'Someone'} performed an activity`;
        let enrichedMeta = { ...(activity.metadata || {}) } as any;

        try {
          if (activityType === 'mention') {
            const meta = activity.metadata || {};
            const source = meta.source;
            if (source === 'list_comment' && meta.list_id) {
              const listRow = listMap.get(meta.list_id);
              enrichedMeta = { ...enrichedMeta, list_name: listRow?.name };
              message = `${profile?.name || 'Someone'} mentioned you in a list discussion`;
            } else if (meta.review_id) {
              const reviewRow = reviewMap.get(meta.review_id);
              enrichedMeta = {
                ...enrichedMeta,
                artist_name: reviewRow?.artist_name || meta.artist_name,
                tour_name: reviewRow?.tour_name || meta.tour_name,
              };
              const subject = reviewRow?.tour_name || reviewRow?.artist_name || meta.tour_name || meta.artist_name;
              message = subject
                ? `${profile?.name || 'Someone'} mentioned you in a comment on ${subject}`
                : `${profile?.name || 'Someone'} mentioned you in a comment`;
            } else {
              message = `${profile?.name || 'Someone'} mentioned you in a comment`;
            }
          } else if (activityType === 'follow') {
            message = `${profile?.name || 'Someone'} started following you`;
            enrichedMeta.follower_username = profile?.username;
          } else if (activityType === 'comment' && activity.metadata?.review_id) {
            const reviewRow = reviewMap.get(activity.metadata.review_id);
            enrichedMeta = {
              ...enrichedMeta,
              review_id: activity.metadata.review_id,
              artist_name: reviewRow?.artist_name,
              tour_name: reviewRow?.tour_name,
            };
            const artistPart = reviewRow?.artist_name || 'an artist';
            const tourPart = reviewRow?.tour_name ? ` (${reviewRow.tour_name})` : '';
            message = `${profile?.name || 'Someone'} commented on your review of ${artistPart}${tourPart}`;
          } else if (activityType === 'like') {
            const itemType = activity.metadata?.item_type;
            const itemId = activity.metadata?.item_id || activity.metadata?.review_id;
            if (itemType === 'review' && itemId) {
              const reviewRow2 = reviewMap.get(itemId);
              enrichedMeta = {
                ...enrichedMeta,
                review_id: itemId,
                artist_name: reviewRow2?.artist_name,
                tour_name: reviewRow2?.tour_name,
              };
              const artistPart = reviewRow2?.artist_name || 'an artist';
              const tourPart = reviewRow2?.tour_name ? ` (${reviewRow2.tour_name})` : '';
              message = `${profile?.name || 'Someone'} liked your review of ${artistPart}${tourPart}`;
            } else if (itemType === 'list' && itemId) {
              const listRow = listMap.get(itemId);
              enrichedMeta = {
                ...enrichedMeta,
                list_id: itemId,
                list_name: listRow?.name,
                item_id: itemId,
              };
              message = `${profile?.name || 'Someone'} liked your list "${listRow?.name || 'Untitled'}"`;
            }
          } else if (activityType === 'review') {
            const artistName = activity.metadata?.artist_name;
            enrichedMeta = { ...enrichedMeta, artist_name: artistName };
            message = `${profile?.name || 'Someone'} reviewed ${artistName || 'a concert'}`;
          } else if (activityType === 'concert_announced') {
            const { artist_name, venue, city, date } = activity.metadata || {};
            enrichedMeta = { ...enrichedMeta, ...activity.metadata };
            message = `New concert: ${artist_name} at ${venue}, ${city}`;
            if (date) {
              message += ` on ${new Date(date).toLocaleDateString()}`;
            }
          } else if (activityType === 'tour_announced') {
            const { artist_name, tour_name, tour_id, artist_id, tour_year } = activity.metadata || {};
            const artistImage = artist_id ? artistMap.get(artist_id)?.image || null : null;

            enrichedMeta = {
              ...enrichedMeta,
              ...activity.metadata,
              tour_id,
              artist_id,
              artist_image: artistImage || activity.metadata?.tour_image || null,
              isArtistNotification: true,
            };
            message = `🎸 ${artist_name || 'An artist'} announced a new tour: ${tour_name || 'Untitled'}${tour_year ? ` (${tour_year})` : ''}`;
          }
        } catch (e) {
          console.warn('Notification enrichment failed:', e);
        }

        const createdAtRaw = typeof activity.created_at === 'string' ? activity.created_at : null;

        return {
          id: activity.id,
          type: activityType as any,
          message,
          timestamp: formatTimestamp(activity.created_at),
          createdAt: activity.created_at,
          read: resolveRead(activity.id, createdAtRaw),
          serverBacked: false,
          user: {
            username: profile?.username || 'unknown',
            name: profile?.name || 'Unknown User',
            avatar: resolveAvatarUrl(profile, null, 'notifications-feed-activity'),
          },
          metadata: enrichedMeta,
        } as Notification;
      });

      const directNotifications: Notification[] = directList.map((notificationRow) => {
        const relatedId = notificationRow.related_id || undefined;
        const createdAtRaw = typeof notificationRow.created_at === 'string' ? notificationRow.created_at : null;

        let actorProfile: ProfileLite | null = null;
        const enrichedMeta: Record<string, any> = {};
        let type = notificationRow.type;
        let message = notificationRow.message || 'You have a new notification';

        if ((type === 'follow_request' || type === 'follow_request_approved') && relatedId) {
          // Producers store the actor's profile id directly in related_id.
          actorProfile = profileMap.get(relatedId) || null;
        } else if (type !== 'artist_nearby_show') {
          const actorMatch = /^(.*?)\s+(liked|commented|replied|mentioned|started following|sent you)/i.exec(message);
          const actorName = actorMatch?.[1]?.trim();
          if (actorName) actorProfile = nameProfileMap.get(actorName.toLowerCase()) || null;
        }

        if ((type === 'comment_on_review' || type === 'comment_reply' || type === 'mention') && relatedId) {
          const reviewRow = reviewMap.get(relatedId);
          enrichedMeta.review_id = relatedId;
          enrichedMeta.artist_name = reviewRow?.artist_name;
          enrichedMeta.tour_name = reviewRow?.tour_name;
        }

        if (type === 'artist_nearby_show' && relatedId) {
          // related_id is the concerts.id written by notify-nearby-shows.
          const concertRow = concertMap.get(relatedId);
          enrichedMeta.concert_id = relatedId;
          enrichedMeta.concert_slug = concertRow?.slug || null;
          enrichedMeta.venue = concertRow?.venue || null;
          enrichedMeta.city = concertRow?.city || null;
          enrichedMeta.date = concertRow?.date || null;
          enrichedMeta.artist_id = concertRow?.artist_id || null;
          if (concertRow?.artist_id) {
            const artistRow = artistMap.get(concertRow.artist_id);
            enrichedMeta.artist_name = artistRow?.name || null;
            enrichedMeta.artist_image = artistRow?.image || concertRow?.image || null;
          } else {
            enrichedMeta.artist_image = concertRow?.image || null;
          }
          enrichedMeta.isArtistNotification = true;
        }

        if (type === 'follow_request_approved') {
          if (actorProfile?.username) enrichedMeta.actor_username = actorProfile.username;
          message = `${actorProfile?.name || 'Someone'} ${message}`;
        }

        if (type === 'new_follower') {
          type = 'follow';
          if (actorProfile?.username) enrichedMeta.follower_username = actorProfile.username;
        }

        if (type === 'follow_request' && actorProfile?.username) {
          enrichedMeta.actor_username = actorProfile.username;
        }

        return {
          id: notificationRow.id,
          type: type as any,
          message,
          timestamp: formatTimestamp(notificationRow.created_at),
          createdAt: notificationRow.created_at,
          read: resolveRead(notificationRow.id, createdAtRaw, notificationRow.read),
          serverBacked: true,
          user: actorProfile
            ? {
                username: actorProfile.username || 'unknown',
                name: actorProfile.name || 'Unknown User',
                avatar: resolveAvatarUrl(actorProfile, null, 'notifications-feed-direct'),
              }
            : undefined,
          metadata: enrichedMeta,
        } as Notification;
      });

      const notifications = [...activityNotifications, ...directNotifications].sort((a, b) => {
        const aTime = new Date(a.createdAt).getTime();
        const bTime = new Date(b.createdAt).getTime();
        return bTime - aTime;
      });

      // Keyset cursor: only continue if we hit the raw page size (more rows
      // likely exist). Use the oldest raw row's created_at so we don't skip
      // activities filtered out above.
      const nextCursor =
        rawList.length === RAW_PAGE_SIZE || directList.length === RAW_PAGE_SIZE
          ? [rawList[rawList.length - 1]?.created_at, directList[directList.length - 1]?.created_at]
              .filter(Boolean)
              .sort()[0] || null
          : null;

      devLog('[NotificationsPagination] page result', {
        tab: 'notifications',
        rawCount: rawList.length,
        notifications: notifications.length,
        nextCursor,
      });

      return { items: notifications, nextCursor, rawCount: Math.max(rawList.length, directList.length) };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  // Flatten pages + dedupe by id (defensive against any racing inserts)
  const data = useMemo(() => {
    const seen = new Set<string>();
    const out: Notification[] = [];
    let dupes = 0;
    (query.data?.pages || []).forEach((p) =>
      p.items.forEach((n) => {
        if (seen.has(n.id)) {
          dupes++;
          return;
        }
        seen.add(n.id);
        out.push(n);
      })
    );
    if (dupes > 0) {
      devLog('[NotificationsPagination] deduped', { tab: 'notifications', skipped: dupes });
    }
    devLog('[NotificationsPagination] total loaded', { tab: 'notifications', total: out.length });
    return out;
  }, [query.data]);

  // Sign out / user switch: drop the previous user's read state and cached
  // pages so no unread badge can leak across accounts.
  useEffect(() => {
    if (!user?.id) {
      resetSharedState();
      queryClient.removeQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      return;
    }
    if (sharedState.userId && sharedState.userId !== user.id) {
      resetSharedState();
      queryClient.removeQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    }
  }, [user?.id, queryClient]);

  const markAsRead = useCallback(
    (id: string) => {
      sharedState.readIds.add(id);
      persistReadIds();

      // Server-side persistence for rows that own a `read` column.
      if (sharedState.serverBackedIds.has(id) && user?.id) {
        supabase
          .from('notifications')
          .update({ read: true })
          .eq('id', id)
          .eq('user_id', user.id)
          .then(({ error }) => {
            if (error) console.warn('Failed to persist notification read state:', error);
          });
      }

      // Prefix match: the full key also carries the blocked-users signature,
      // which can change after mount. Writing to a single snapshot of the key
      // used to orphan the update and the dot came straight back.
      queryClient.setQueriesData({ queryKey: [...NOTIFICATIONS_QUERY_KEY, user?.id] }, (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((p: NotificationPage) => ({
            ...p,
            items: p.items.map((n) => (n.id === id ? { ...n, read: true } : n)),
          })),
        };
      });
    },
    [queryClient, queryKey, user?.id]
  );

  const markAllAsRead = useCallback(() => {
    sharedState.lastSeenAt = new Date().toISOString();
    persistLastSeenAt();

    data.forEach((n) => sharedState.readIds.add(n.id));
    persistReadIds();

    if (user?.id) {
      supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .eq('read', false)
        .then(({ error }) => {
          if (error) console.warn('Failed to persist bulk notification read state:', error);
        });
    }

    queryClient.setQueriesData({ queryKey: [...NOTIFICATIONS_QUERY_KEY, user?.id] }, (old: any) => {
      if (!old?.pages) return old;
      return {
        ...old,
        pages: old.pages.map((p: NotificationPage) => ({
          ...p,
          items: p.items.map((n) => ({ ...n, read: true })),
        })),
      };
    });
  }, [queryClient, data, queryKey, user?.id]);

  const unreadCount = useMemo(() => data.filter((n) => !n.read).length, [data]);

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: !!query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    refetch: query.refetch,
    unreadCount,
    markAsRead,
    markAllAsRead,
  };
};
